<template>
  <v-container class="contact-page" fluid>
    <div class="head-wrap mb-4">
      <div>
        <h1 class="headline mb-1">Government &amp; Treatment Rights <span class="thai">สิทธิประโยชน์การรักษา</span></h1>
        <p class="subhead mb-0">Manage government/education benefits and general treatment right entries.</p>
      </div>
      <v-btn v-if="canAdd" class="setup-btn" color="primary" @click="$router.push({name:'special-right-create'})">
        <v-icon left small>mdi-plus</v-icon>
        Create Entry
      </v-btn>
    </div>

    <v-card class="table-card">
      <div class="category-bar">
        <button
          v-for="type in rightsTypeFilters"
          :key="type.value"
          type="button"
          class="category-chip"
          :class="{'active': selectedRightsType === type.value}"
          @click="onRightsTypeChange(type.value)"
        >
          {{ type.label }}
        </button>
      </div>

      <div class="filter-bar">
        <div class="search-wrap">
          <v-text-field
            v-model="searchText"
            append-icon="mdi-magnify"
            outlined
            dense
            hide-details
            label="Search by title..."
            clearable
            @input="onSearch"
          />
        </div>
        <v-select v-if="isSuperAdmin"
          v-model="selectedOrg"
          :items="orgOptionsWithAll"
          outlined
          dense
          hide-details
          item-text="text"
          item-value="value"
          class="small-select"
          :disabled="isBranchLocked"
          placeholder="All Branches"
          @input="onOrgFilterChange"
        />
        <v-btn outlined class="filter-btn" :class="{'has-filtered': hasAppliedFilters}" @click="handleFilterButtonClick">
          <v-icon small left>{{ hasAppliedFilters ? 'mdi-close' : 'mdi-filter-variant' }}</v-icon>
          {{ hasAppliedFilters ? 'Clear' : 'Filter' }}
        </v-btn>
      </div>

      <v-data-table
        :loading="isPageLoading"
        :headers="visibleHeaders"
        :items="list"
        :items-per-page="10"
        hide-default-footer
        class="contact-table"
      >
        <template v-slot:item.no="{index, item}">{{ rowNo(index, item) }}</template>

        <template v-slot:item.rights_type="{item}">
          <span class="type-pill">{{ rightsTypeLabel(item.rights_type) }}</span>
        </template>

        <template v-slot:item.title="{item}">
          <div class="title-wrap">
            <strong>{{ item.title_en || item.title_th || '-' }}</strong>
            <div class="title-sub">{{ item.title_th || '-' }}</div>
          </div>
        </template>

        <template v-slot:item.branch="{item}">
          <span class="branch-pill">{{ item.org_codename || '-' }}</span>
        </template>

        <template v-slot:item.action="{item}">
          <button v-if="canEdit" class="icon-btn" @click.stop="goToEdit(item)"><v-icon size="16">mdi-pencil-outline</v-icon></button>
          <button v-if="canDelete" class="icon-btn ml-2" @click.stop="remove(item)"><v-icon size="16">mdi-delete-outline</v-icon></button>
        </template>
      </v-data-table>

      <div class="table-foot">
        Showing 1 to {{ list.length }} of {{ list.length }} entries
      </div>
    </v-card>

  </v-container>
</template>

<script>
import {mapActions, mapState} from 'vuex'
import {hasPermision} from '@/helper/helper'

export default {
  data() {
    return {
      searchText: '',
      selectedOrg: '',
      selectedRightsType: '',
      localLoading: false,
      headers: [
        {text:'NO.', value:'no', sortable:false, width:70},
        {text:'RIGHTS TYPE', value:'rights_type', sortable:false, width:250},
        {text:'TITLE', value:'title', sortable:false},
        {text:'BRANCH', value:'branch', sortable:false, width:140},
        {text:'ACTIONS', value:'action', sortable:false, width:120, align:'center'}
      ]
    }
  },
  computed: {
    ...mapState('specialRights',['list','isLoading','orgOptions']),
    ...mapState('auth', ['user']),
    canAdd() { return hasPermision('CanAddSpecialRight') },
    canEdit() { return hasPermision('CanEditSpecialRight') },
    canDelete() { return hasPermision('CanDeleteSpecialRight') },
    isBranchLocked() {
      return !this.isSuperAdmin || this.orgOptions.length === 1
    },
    isSuperAdmin() {
      const currentUser = this.user || {}
      return !!currentUser.is_superuser
    },
    orgOptionsWithAll() {
      return [{text: 'All Branches', value: ''}, ...this.orgOptions]
    },
    rightsTypeFilters() {
      return [
        { label: 'All', value: '' },
        { label: 'Government Officer', value: 'government' },
        { label: 'Academic & Educational', value: 'education' },
        { label: 'General Treatment Benefits', value: 'treatment' }
      ]
    },
    visibleHeaders() {
      if (this.isSuperAdmin) return this.headers
      return this.headers.filter(item => item.value !== 'branch')
    },
    hasAppliedFilters() {
      const hasSearch = !!(this.searchText || '').trim()
      const hasRightsType = !!(this.selectedRightsType || '').trim()
      const hasBranchFilter = this.isSuperAdmin && !!(this.selectedOrg || '').trim()
      return hasSearch || hasRightsType || hasBranchFilter
    },
    isPageLoading() {
      return this.localLoading || this.isLoading
    }
  },
  async created() {
    this.localLoading = true
    try {
      this.setResource('special-rights')
      await this.fetchOrgOptions()
      this.setTextSearch('')
      if (!this.isSuperAdmin) {
        this.selectedOrg = this.getUserOrgCode()
        this.setFilterOrgCode(this.selectedOrg)
      } else if (this.orgOptions.length === 1) {
        this.selectedOrg = this.orgOptions[0].value
        this.setFilterOrgCode(this.selectedOrg)
      } else {
        this.setFilterOrgCode('')
      }
      this.setFilterRightsType('')
      await this.fetch()
    } finally {
      this.localLoading = false
    }
  },
  methods: {
    ...mapActions('specialRights',['setResource','fetch','setTextSearch','setFilterOrgCode','setFilterRightsType','fetchOrgOptions','deleteById']),
    getUserOrgCode() {
      const user = this.user || {}
      return user.org_code || user.org_codename || user.org || ''
    },
    rowNo(index, item) {
      let rowIndex = Number(index)
      if (Number.isNaN(rowIndex)) {
        const fallbackIndex = (this.list || []).findIndex((listItem) => {
          if (item && item.id && listItem.id) return String(listItem.id) === String(item.id)
          return listItem === item
        })
        rowIndex = fallbackIndex >= 0 ? fallbackIndex : 0
      }
      return rowIndex + 1
    },
    rightsTypeLabel(type) {
      if (type === 'government') return 'Government Officer'
      if (type === 'education') return 'Academic & Educational'
      if (type === 'treatment') return 'General Treatment Benefits'
      return '-'
    },
    async onSearch() {
      this.localLoading = true
      try {
        this.setTextSearch(this.searchText || '')
        await this.fetch()
      } finally {
        this.localLoading = false
      }
    },
    async onOrgFilterChange() {
      this.localLoading = true
      try {
        this.setFilterOrgCode(this.selectedOrg || '')
        await this.fetch()
      } finally {
        this.localLoading = false
      }
    },
    async onRightsTypeChange(type) {
      this.selectedRightsType = type
      this.localLoading = true
      try {
        this.setFilterRightsType(type)
        await this.fetch()
      } finally {
        this.localLoading = false
      }
    },
    async handleFilterButtonClick() {
      if (!this.hasAppliedFilters) return

      this.localLoading = true
      try {
        this.searchText = ''
        this.selectedRightsType = ''
        this.selectedOrg = this.isSuperAdmin ? '' : this.getUserOrgCode()
        this.setTextSearch('')
        this.setFilterRightsType('')
        this.setFilterOrgCode(this.selectedOrg)
        await this.fetch()
      } finally {
        this.localLoading = false
      }
    },
    goToEdit(item){
      this.$router.push({name:'special-right-edit', params:{id:item.id}})
    },
    async remove(item){
      if(!window.confirm('Delete this record?')) return
      this.localLoading = true
      try {
        const ok = await this.deleteById(item.id)
        if(ok) await this.fetch()
      } finally {
        this.localLoading = false
      }
    }
  }
}
</script>

<style scoped>
.contact-page { background:#f7f9fc; min-height:calc(100vh - 120px); }
.head-wrap { display:flex; justify-content:space-between; align-items:flex-start; gap:12px; }
.headline { font-size:30px!important; color:#0f1f3b; }
.thai { color:#64748b; font-size:15px; font-weight:500; margin-left:6px; }
.subhead { color:#64748b; font-size:13px; }
.setup-btn { text-transform:none; font-weight:700; border-radius:10px; }
.table-card { border:1px solid #e6e9f0; border-radius:10px; box-shadow:none; overflow:hidden; }
.category-bar { display:flex; gap:10px; padding:14px; border-bottom:1px solid #edf2fb; flex-wrap:wrap; }
.category-chip { border:1px solid #dfe7f6; background:#fff; color:#5f7397; border-radius:999px; padding:8px 18px; font-size:14px; font-weight:700; cursor:pointer; }
.category-chip.active { background:#eef4ff; border-color:#bcd3ff; color:#1e49ba; }
.filter-bar { display:flex; gap:10px; padding:14px; border-bottom:1px solid #edf2fb; }
.search-wrap { flex:3; }
.small-select { flex:1 }
.filter-btn { text-transform:none; font-weight:600; min-width:96px; }
.filter-btn.has-filtered { border-color:#f3b5b5 !important; color:#c62828 !important; background:#fdecec; }
.type-pill { display:inline-flex; background:#eef4ff; color:#2f6fed; border-radius:7px; padding:6px 10px; font-size:12px; font-weight:700; line-height:1.2; }
.branch-pill { display:inline-flex; align-items:center; font-size:11.5px; font-weight:700; letter-spacing:.2px; padding:4px 9px; border-radius:6px; background:#eef1f6; color:#5b6472; }
.title-wrap { line-height:1.25; }
.title-sub { color:#7284a5; font-size:13px; }
.icon-btn { width:30px; height:30px; border-radius:7px; border:1px solid #e6e9f0; background:#fff; }
.table-foot { padding:14px 16px; border-top:1px solid #edf2fb; color:#64748b; font-size:12px; }
.contact-table::v-deep .v-data-table-header th { color:#7c8aa5!important; font-size:12px; font-weight:700; }
.contact-table::v-deep td { font-size:14px; }
</style>
